import { TextInput } from '@gurupay/gp-ui-library';
import { useFXIbanValidation } from '@pages/payments/hooks/useSwiftIbanValidations.ts';
import { memo, useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { FXFormFieldConfig } from 'types/operations';

import { useValidationRules } from '../hooks/useValidationRules.ts';

interface BaseFormFieldProps {
	fieldKey: string;
	fieldConfig: FXFormFieldConfig;
	disabled?: boolean;
	onIbanBicMaskChange?: (bicMask: string | null) => void;
}

export const TextFormField = memo(
	({ fieldKey, fieldConfig, disabled, onIbanBicMaskChange }: BaseFormFieldProps) => {
		const { t } = useTranslation();
		const { control } = useFormContext();
		const rules = useValidationRules(fieldKey, fieldConfig);
		const isIbanField = fieldKey === 'beneficiary.account';
		const { validate: validateSwiftIban, bicMask: ibanBicMask } =
			useFXIbanValidation(isIbanField);

		useEffect(() => {
			if (isIbanField) {
				onIbanBicMaskChange?.(ibanBicMask);
			}
		}, [isIbanField, ibanBicMask, onIbanBicMaskChange]);

		const { field, fieldState } = useController({
			name: fieldKey,
			control,
			rules: isIbanField ? { ...rules, validate: validateSwiftIban } : rules,
		});

		return (
			<div>
				<TextInput
					{...field}
					className="animate-fade-in max-w-125"
					error={fieldState.error?.message}
					isDisabled={disabled}
					isRequired={fieldConfig.required}
					label={t(`payments.newPayment.additionalData.fx.form.${fieldConfig.label}`)}
					placeholder={fieldConfig.placeholder}
				/>
			</div>
		);
	},
);

TextFormField.displayName = 'TextFormField';
